import { builder } from '@builder.io/sdk';

import { getErrorMessage, tryCatch } from 'utils/errorUtils';

import type { GetContentOptions } from '@builder.io/sdk';
import type { TemplateModelData } from 'types/types';

const BUILDER_API_KEY = process.env.NEXT_PUBLIC_BUILDER_API_KEY || '';

builder.init(BUILDER_API_KEY);

type GetBuilderDataOptions = {
  fields?: GetContentOptions['fields'];
  query?: GetContentOptions['query'];
  limit?: GetContentOptions['limit'];
  id?: string;
  slug?: string;
  singleItem?: boolean;
  sort?: GetContentOptions['sort'];
  userAttributes?: string;
  offset?: number;
};

export const getBuilderDataModel = async (model: string, options?: GetBuilderDataOptions) => {
  const { fields, query = '', slug } = options ?? {};

  const result = await tryCatch(
    builder
      .get(model, {
        userAttributes: {
          urlPath: slug,
        },
        prerender: false,
        cachebust: true,
        options: {
          query,
        },
        fields,
      })
      .toPromise(),
  );

  if (result.error) {
    console.error(`Error fetching Builder data model ${model}: ${getErrorMessage(result.error)}`);

    return null;
  }

  return result.data;
};

export const getBuilderDataModelFetch = async (builderModel: string, options?: GetBuilderDataOptions) => {
  const {
    fields = '',
    limit = 100,
    query = '',
    sort = '',
    singleItem = false,
    userAttributes = '',
    offset = 0,
  } = options ?? {};
  const stringifiedLimit = `&limit=${limit}`;

  const fetchResult = await tryCatch(
    fetch(
      `https://cdn.builder.io/api/v3/content/${builderModel}?apiKey=${BUILDER_API_KEY}${fields}${stringifiedLimit}${query}${sort}${userAttributes}&offset=${offset}&cachebust=true`,
    ),
  );

  if (fetchResult.error) {
    console.error(`Error fetching Builder data model ${builderModel}: ${getErrorMessage(fetchResult.error)}`);

    return null;
  }

  const jsonResult = await tryCatch(fetchResult.data.json());

  if (jsonResult.error) {
    console.error(`Error parsing JSON for Builder data model ${builderModel}: ${getErrorMessage(jsonResult.error)}`);

    return null;
  }

  const data = jsonResult.data;

  return singleItem ? data.results?.[0] ?? null : data.results ?? null;
};

export const getDraftPost = async (builderModel: string | null, id: string) => {
  const fetchResult = await tryCatch(
    fetch(
      `https://cdn.builder.io/api/v3/content/${builderModel}/${id}?apiKey=${BUILDER_API_KEY}&preview=true&noCache=true&cachebust=true&includeRefs=true`,
      { cache: 'no-store' },
    ),
  );

  if (fetchResult.error) {
    console.error(
      `Error fetching draft post for model ${builderModel}, id ${id}: ${getErrorMessage(fetchResult.error)}`,
    );

    return null;
  }

  const jsonResult = await tryCatch(fetchResult.data.json());

  if (jsonResult.error) {
    console.error(
      `Error parsing JSON for draft post model ${builderModel}, id ${id}: ${getErrorMessage(jsonResult.error)}`,
    );

    return null;
  }

  return jsonResult.data || null;
};

export const generateStaticParamsHelperFunction = async (
  model: string,
  initialPath: string,
  offset = 0,
  isHealthcareInsights = false,
  removePathnamePrefix = true,
  delayMs = 0,
): Promise<{ slug: string[] }[]> => {
  const variableToExcludeLearningCenterContentOnly = encodeURIComponent('LC Only');
  const query = isHealthcareInsights
    ? `&query.data.templateInfo.siteAvailability.$ne=${variableToExcludeLearningCenterContentOnly}`
    : '';

  const fetchResult = await tryCatch(
    getBuilderDataModelFetch(model, {
      fields: '&fields=data.url',
      query,
      offset,
    }),
  );

  if (fetchResult.error) {
    console.error(`Error generating static params for model ${model}: ${getErrorMessage(fetchResult.error)}`);

    return [];
  }

  const slugs = fetchResult.data as TemplateModelData[];

  const paths = slugs.map(({ data }) => {
    let urlPath = data?.url || '';
    let slug: string[];

    if (removePathnamePrefix) {
      const pathWithoutPrefix = urlPath.replace(initialPath, '') || '';
      slug = pathWithoutPrefix === '' ? [] : pathWithoutPrefix.split('/');
    } else {
      slug = urlPath === '' ? [] : urlPath.split('/').filter(Boolean);
    }

    if (model === 'template-claims-lesson') {
      urlPath = urlPath.replaceAll('claims-lesson', 'case-study');
    }

    return { slug };
  });

  if (slugs.length < 100) {
    return paths;
  }

  if (delayMs > 0) {
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }

  const nextPathsResult = await tryCatch(
    generateStaticParamsHelperFunction(
      model,
      initialPath,
      offset + 100,
      isHealthcareInsights,
      removePathnamePrefix,
      delayMs,
    ),
  );

  if (nextPathsResult.error) {
    console.error(
      `Error in recursive generateStaticParamsHelperFunction call: ${getErrorMessage(nextPathsResult.error)}`,
    );

    return paths;
  }

  return [...paths, ...nextPathsResult.data];
};
